import { Injectable } from "@angular/core";
import type { ParamMap } from "@angular/router";

const STORAGE_KEY = "handshake_canonical_reads_override";

@Injectable({
  providedIn: "root",
})
export class CanonicalPaymentReadOverrideService {
  private normalize(raw: string): "true" | "false" | undefined {
    const lower = raw.trim().toLowerCase();
    if (["true", "1", "yes"].includes(lower)) {
      return "true";
    }
    if (["false", "0", "no"].includes(lower)) {
      return "false";
    }
    return undefined;
  }

  /** Reads `canonicalReads` from the route and stores override for payment-provider read mode on wallet API calls. */
  applyFromQueryParamMap(map: ParamMap): void {
    if (!map.has("canonicalReads")) {
      return;
    }
    const raw = map.get("canonicalReads")?.trim() ?? "";
    if (raw === "") {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    const normalized = this.normalize(raw);
    if (normalized === "true") {
      try {
        sessionStorage.setItem(STORAGE_KEY, "true");
      } catch {
        /* ignore */
      }
      return;
    }
    if (normalized === "false") {
      try {
        sessionStorage.setItem(STORAGE_KEY, "false");
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Value appended as `canonicalReads` query on wallet GETs when the user set a payment-provider read override. */
  getQueryValueForHttp(): string | undefined {
    try {
      const v = sessionStorage.getItem(STORAGE_KEY);
      return v === "true" || v === "false" ? v : undefined;
    } catch {
      return undefined;
    }
  }

  getCurrentOverride(): boolean | undefined {
    const v = this.getQueryValueForHttp();
    if (v === "true") return true;
    if (v === "false") return false;
    return undefined;
  }

  setOverride(enabled: boolean | undefined): void {
    if (enabled == null) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, enabled ? "true" : "false");
    } catch {
      /* ignore */
    }
  }
}
